import React from 'react';
import { Box, Typography, Button, useTheme, Alert, Paper } from '@mui/material';
import { Link } from 'react-router-dom';
import { StepperComponentProps } from '../types';
import { useNewAccountStepperContext } from '../context/NewAccountStepperContext';
import { StatefulButton } from './StatefulButton';
import { useLoginContext } from '../../../../contexts/LoginContext';
import { stepperStyles } from '../../shared/styles';
import JsonViewer, { JsonValue } from '../../shared/components/JsonViewer';
import ConfirmationInfoItem from '../../shared/components/ConfirmationInfoItem/ConfirmationInfoItem';
import ProfileConfirmation from '../../shared/components/ProfileConfirmation/ProfileConfirmation';

const ConfirmationScreen: React.FC<StepperComponentProps> = ({
  description,
  label,
}) => {
  const { handleSave, error } = useNewAccountStepperContext();
  const theme = useTheme();
  const { user } = useLoginContext();

  return (
    <Box sx={stepperStyles.pageContainer}>
      <Box sx={stepperStyles.centeredContent}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={stepperStyles.pageTitle(theme)}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={stepperStyles.pageDescription(theme)}>
          {description}
        </Typography>

        <Paper elevation={3} sx={stepperStyles.elevatedCard(theme)}>
          {/* User and Profile Summary Section */}
          <Box sx={stepperStyles.summarySection}>
            <Box sx={stepperStyles.flexColumnGap(3)}>
              {/* User Information Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  Account Information
                </Typography>
                <Box sx={stepperStyles.userInfoGrid}>
                  <ConfirmationInfoItem
                    title={'Full Name'}
                    content={user?.name || 'Not specified'}
                  />
                  <ConfirmationInfoItem
                    title={'Email Address'}
                    content={user?.emailAddress || 'Not specified'}
                  />
                  <ConfirmationInfoItem
                    title={'Email Format'}
                    content={
                      user?.contentType === 'text/html'
                        ? 'HTML'
                        : user?.contentType === 'text/plain'
                        ? 'Plain Text'
                        : user?.contentType || 'Not specified'
                    }
                  />
                </Box>
              </Box>

              {/* Profiles Section */}
              {user?.extProfiles && user.extProfiles.length > 0 && (
                <Box>
                  <Box sx={stepperStyles.flexColumnGap(2)}>
                    {user.extProfiles.map((profile, index) => (
                      <ProfileConfirmation key={index} profile={profile} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Instructions Section */}
          <Typography
            variant="body1"
            sx={{ ...stepperStyles.instructionText(theme), mb: 2 }}
          >
            Click the button below to create your email notification account.
            You will receive an email with a confirmation link. Please click the
            link to confirm your account and complete the setup process.
          </Typography>
          <Typography variant="body2" sx={stepperStyles.helperNote(theme)}>
            Note: You will not receive notifications until your account is
            confirmed.
          </Typography>

          {/* Action Button */}
          <Box sx={stepperStyles.centeredActionContainer}>
            <StatefulButton
              variant="contained"
              size="large"
              onClick={handleSave}
              sx={{
                ...stepperStyles.elevatedButton(theme),
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
              }}
            >
              Create Account & Send Confirmation
            </StatefulButton>
          </Box>

          {/* Error Section */}
          {error && (
            <Alert
              severity="error"
              icon={false}
              sx={{
                ...stepperStyles.paperCard(theme, 2),
                ...stepperStyles.errorAlert(theme),
              }}
            >
              <Box sx={stepperStyles.fullWidthCenteredContent}>
                <Typography
                  variant="subtitle1"
                  sx={stepperStyles.errorTitle(theme)}
                >
                  Account Creation Failed
                </Typography>
                <Typography
                  variant="body2"
                  sx={stepperStyles.errorMessage(theme)}
                >
                  We encountered an issue while creating your account:
                </Typography>
                <Typography variant="body2" sx={stepperStyles.errorCode(theme)}>
                  {error}
                </Typography>

                <Button
                  component={Link}
                  to={`/resetpassword?email=${user?.emailAddress ?? ''}`}
                  color="error"
                  size="medium"
                  variant="contained"
                  sx={stepperStyles.buttonWithIcon}
                >
                  Reset Password
                </Button>
              </Box>
            </Alert>
          )}
        </Paper>

        {/* Debug Panel - Development Only */}
        {import.meta.env.DEV && (
          <Paper
            elevation={2}
            sx={{ mt: 3, ...stepperStyles.debugPanel(theme) }}
          >
            <Typography variant="h6" sx={stepperStyles.debugTitle(theme)}>
              <span role="img" aria-label="wrench">
                🔧
              </span>{' '}
              Debug Information (Development Only)
            </Typography>

            <JsonViewer
              data={user as JsonValue}
              title="User Object"
              initialExpanded={false}
              copyable={true}
            />
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default ConfirmationScreen;
